import React, { useEffect, useRef, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import {
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  multiFactor,
  signOut,
} from 'firebase/auth';
import { ADMIN_MFA_MODE, ADMIN_MFA_REQUIRED, auth, db } from './firebase';

function obterMensagemErroEnvioMfa(error) {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  if (error?.code === 'auth/captcha-check-failed') {
    return `O domínio ${hostname || 'deste painel'} ainda não está autorizado no Firebase para SMS. Vá em Authentication > Configurações > Domínios autorizados e adicione este domínio.`;
  }

  if (error?.code === 'auth/invalid-phone-number') {
    return 'Informe um celular válido com DDD para ativar a segurança.';
  }

  if (error?.code === 'auth/too-many-requests') {
    return 'Muitas tentativas seguidas. Aguarde alguns minutos e tente novamente.';
  }

  if (error?.code === 'auth/requires-recent-login') {
    return 'Por segurança, saia e entre novamente antes de ativar o MFA desta conta administrativa.';
  }

  return 'Não foi possível enviar o código de segurança agora. Verifique o número e tente novamente.';
}

function normalizarTelefoneMfa(valor) {
  const texto = String(valor || '').trim();

  if (!texto) {
    return '';
  }

  if (texto.startsWith('+')) {
    return texto;
  }

  const digitos = texto.replace(/\D/g, '');

  if (digitos.length === 10 || digitos.length === 11) {
    return `+55${digitos}`;
  }

  if (digitos.startsWith('55') && digitos.length >= 12) {
    return `+${digitos}`;
  }

  return `+${digitos}`;
}

function mascararTelefone(valor) {
  const digitos = String(valor || '').replace(/\D/g, '');
  if (digitos.length < 4) {
    return valor || '';
  }

  return `***${digitos.slice(-4)}`;
}

function AdminSecurityGate({ user, contextoAcesso = null, children }) {
  const [status, setStatus] = useState('checking');
  const [telefone, setTelefone] = useState('');
  const [codigo, setCodigo] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);
  const recaptchaRef = useRef(null);
  const recaptchaContainerId = 'mfa-enroll-recaptcha';

  useEffect(() => {
    let ativo = true;

    async function validarCamadaAdministrativa() {
      const perfisAtivos = Array.isArray(contextoAcesso?.perfisAtivos) ? contextoAcesso.perfisAtivos : [];
      const requerCamadaSegura = contextoAcesso?.requiresSecurityGate === true ||
        perfisAtivos.includes('admin') ||
        perfisAtivos.includes('tecnico');

      if (!requerCamadaSegura) {
        setStatus('ready');
        return;
      }

      setStatus('checking');

      try {
        const configSnap = await getDoc(doc(db, 'configuracoes', 'gerais'));

        if (!ativo) {
          return;
        }

        const mfaObrigatorio = ADMIN_MFA_MODE === 'disabled'
          ? false
          : ADMIN_MFA_REQUIRED || configSnap.data()?.mfa_admin_obrigatorio === true;

        await auth.currentUser?.reload();
        const usuarioAtual = auth.currentUser;
        const fatores = usuarioAtual ? multiFactor(usuarioAtual).enrolledFactors : [];

        if (!mfaObrigatorio || fatores.length > 0) {
          setStatus('ready');
          return;
        }

        setTelefone(usuarioAtual?.phoneNumber || contextoAcesso?.telefone || '');
        setStatus('enroll');
      } catch (error) {
        console.error('Falha ao validar MFA do painel interno:', error);
        if (!ativo) {
          return;
        }

        try {
          await auth.currentUser?.reload();
        } catch (reloadError) {
          console.warn('Não foi possível atualizar a sessão durante a validação de MFA:', reloadError);
        }

        const usuarioAtual = auth.currentUser;
        const fatores = usuarioAtual ? multiFactor(usuarioAtual).enrolledFactors : [];

        if (ADMIN_MFA_REQUIRED && fatores.length === 0) {
          setTelefone(usuarioAtual?.phoneNumber || contextoAcesso?.telefone || '');
          setStatus('enroll');
          return;
        }

        setStatus('ready');
      }
    }

    validarCamadaAdministrativa();

    return () => {
      ativo = false;
      if (recaptchaRef.current) {
        recaptchaRef.current.clear();
        recaptchaRef.current = null;
      }
    };
  }, [contextoAcesso?.requiresSecurityGate, contextoAcesso?.telefone, contextoAcesso?.perfisAtivos, user.uid]);

  async function prepararRecaptcha() {
    if (recaptchaRef.current) {
      return recaptchaRef.current;
    }

    const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: 'invisible',
    });

    await verifier.render();
    recaptchaRef.current = verifier;
    return verifier;
  }

  async function enviarCodigo() {
    const telefoneNormalizado = normalizarTelefoneMfa(telefone);

    if (!telefoneNormalizado || telefoneNormalizado.length < 12) {
      setErro('Informe um celular válido com DDD para ativar a segurança.');
      return;
    }

    setCarregando(true);
    setErro('');
    setMensagem('');

    try {
      const usuarioAtual = auth.currentUser;
      if (!usuarioAtual) {
        throw new Error('Sessão expirada. Entre novamente.');
      }

      const verifier = await prepararRecaptcha();
      const multiFactorUser = multiFactor(usuarioAtual);
      const multiFactorSession = await multiFactorUser.getSession();
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const novoVerificationId = await phoneAuthProvider.verifyPhoneNumber({
        phoneNumber: telefoneNormalizado,
        session: multiFactorSession,
      }, verifier);

      setTelefone(telefoneNormalizado);
      setVerificationId(novoVerificationId);
      setMensagem(`Código enviado para ${mascararTelefone(telefoneNormalizado)}.`);
    } catch (error) {
      console.error('Falha ao enviar código MFA:', error);
      setErro(obterMensagemErroEnvioMfa(error));
    } finally {
      setCarregando(false);
    }
  }

  async function confirmarCodigo() {
    if (!verificationId || codigo.trim().length < 6) {
      setErro('Digite o código recebido por SMS para concluir a ativação.');
      return;
    }

    setCarregando(true);
    setErro('');
    setMensagem('');

    try {
      const usuarioAtual = auth.currentUser;
      if (!usuarioAtual) {
        throw new Error('Sessão expirada. Entre novamente.');
      }

      const credencial = PhoneAuthProvider.credential(verificationId, codigo.trim());
      const assertion = PhoneMultiFactorGenerator.assertion(credencial);
      await multiFactor(usuarioAtual).enroll(assertion, 'Painel interno');
      await usuarioAtual.reload();
      setStatus('ready');
    } catch (error) {
      console.error('Falha ao ativar MFA:', error);
      setErro('Código inválido ou expirado. Gere um novo código e tente novamente.');
    } finally {
      setCarregando(false);
    }
  }

  if (status === 'ready') {
    return children;
  }

  if (status === 'checking') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f8f9fa' }}>
        <div style={{ textAlign: 'center', color: '#374151' }}>
          <div style={{ marginBottom: '14px', fontWeight: 'bold' }}>Validando segurança administrativa...</div>
          <div style={{ width: '42px', height: '42px', borderRadius: '50%', border: '4px solid #e5e7eb', borderTopColor: '#e53935', margin: '0 auto', animation: 'spin 0.8s linear infinite' }} />
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ width: '100%', maxWidth: '460px', backgroundColor: 'white', borderRadius: '14px', boxShadow: '0 12px 30px rgba(0,0,0,0.08)', padding: '32px' }}>
        <h2 style={{ marginTop: 0, marginBottom: '12px', color: '#1f2937' }}>Camada Extra de Segurança</h2>
        <p style={{ marginTop: 0, marginBottom: '22px', color: '#6b7280', lineHeight: '1.5' }}>
          Esta conta administrativa precisa ativar a confirmação por SMS antes de acessar o painel.
        </p>

        <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4b5563', marginBottom: '8px' }}>
          Celular de segurança
        </label>
        <input
          type="tel"
          value={telefone}
          onChange={(e) => setTelefone(e.target.value)}
          placeholder="+55 11988887777"
          disabled={carregando}
          style={{ width: '100%', boxSizing: 'border-box', padding: '12px', marginBottom: '14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '15px' }}
        />

        {verificationId && (
          <>
            <label style={{ display: 'block', fontSize: '13px', fontWeight: 'bold', color: '#4b5563', marginBottom: '8px' }}>
              Código recebido por SMS
            </label>
            <input
              type="text"
              value={codigo}
              onChange={(e) => setCodigo(e.target.value)}
              placeholder="Digite o código"
              disabled={carregando}
              style={{ width: '100%', boxSizing: 'border-box', padding: '12px', marginBottom: '14px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '15px' }}
            />
          </>
        )}

        {mensagem && (
          <p style={{ color: '#0f5ca8', backgroundColor: '#edf6ff', border: '1px solid #b9d8f4', padding: '10px', borderRadius: '8px', fontSize: '13px' }}>
            {mensagem}
          </p>
        )}

        {erro && (
          <p style={{ color: '#b91c1c', backgroundColor: '#fef2f2', border: '1px solid #fecaca', padding: '10px', borderRadius: '8px', fontSize: '13px' }}>
            {erro}
          </p>
        )}

        <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={verificationId ? confirmarCodigo : enviarCodigo}
            disabled={carregando}
            style={{ flex: '1 1 180px', padding: '13px', border: 'none', borderRadius: '8px', backgroundColor: '#e53935', color: 'white', fontWeight: 'bold', cursor: 'pointer' }}
          >
            {carregando ? 'Processando...' : (verificationId ? 'Confirmar e liberar acesso' : 'Enviar código')}
          </button>

          <button
            type="button"
            onClick={() => signOut(auth)}
            disabled={carregando}
            style={{ flex: '1 1 140px', padding: '13px', borderRadius: '8px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151', fontWeight: 'bold', cursor: 'pointer' }}
          >
            Sair
          </button>
        </div>

        <div id={recaptchaContainerId} />
      </div>
    </div>
  );
}

export default AdminSecurityGate;
